import { useRef, useState } from 'preact/hooks';
import { t } from '../i18n';
import { navigateToView } from '../navigation';
import { sendResetCode } from '../api';
import type { WidgetConfig } from '../config';
import { getAntiBotEnabled, getAntiBotMinFillMs } from '../antiBot';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim());
}

interface ForgotPasswordViewProps {
  config: WidgetConfig;
}

export function ForgotPasswordView({ config }: ForgotPasswordViewProps) {
  const formStartedAtRef = useRef(Date.now());
  const [email, setEmail] = useState('');
  const [swHoney, setSwHoney] = useState('');
  const submissionPendingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (submissionPendingRef.current) return;
    submissionPendingRef.current = true;
    let submissionSucceeded = false;
    try {
      setError('');
      setEmailError('');
      if (!validateEmail(email)) {
        setEmailError(t('forgotPassword.invalidEmail'));
        return;
      }
      const goToCodeForm = () => {
        try {
          sessionStorage.setItem('sw_reset_email', email.trim());
        } catch {
          /* ignore */
        }
        submissionSucceeded = true;
        navigateToView('reset-password');
      };
      if (getAntiBotEnabled() && (swHoney.trim() !== '' || Date.now() - formStartedAtRef.current < getAntiBotMinFillMs())) {
        goToCodeForm();
        return;
      }
      setLoading(true);
      const res = await sendResetCode(config, email.trim(), {
        sw_honey: swHoney,
        sw_form_ts: formStartedAtRef.current,
      });
      if (res.status === 200 && !res.error && !(res.result as any)?.error_code) {
        goToCodeForm();
      } else {
        const messages = (res as any).messages as Record<string, string> | undefined;
        setError(messages?.E ?? res.error ?? t('api.genericError'));
      }
    } catch {
      setError(t('api.networkError'));
    } finally {
      submissionPendingRef.current = submissionSucceeded;
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate class="mw-400 mx-auto">
      <input
        type="text"
        name="sw_honey"
        value={swHoney}
        onInput={(e) => setSwHoney((e.target as HTMLInputElement).value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
      />
      <div class="mb-3">
        <label class="form-label">{t('forgotPassword.emailLabel')}</label>
        <input
          type="email"
          class={`form-control ${emailError ? 'is-invalid' : ''}`}
          placeholder={t('forgotPassword.emailPlaceholder')}
          value={email}
          onInput={(e) => {
            setEmail((e.target as HTMLInputElement).value);
            setEmailError('');
          }}
          disabled={loading}
        />
        {emailError && <div class="small text-danger mt-1">{emailError}</div>}
      </div>
      {error && <div class="alert alert-danger py-2 small mb-3">{error}</div>}
      <button type="submit" class="btn btn-primary w-100" disabled={loading}>
        {loading ? <span class="spinner-border spinner-border-sm me-2"></span> : null}
        {t('forgotPassword.submit')}
      </button>
      <p class="mt-3 mb-0">
        <a href="#" onClick={(e) => { e.preventDefault(); navigateToView('reset-password'); }} class="small">
          {t('forgotPassword.goToSetPassword')}
        </a>
        {' · '}
        <a href="#" onClick={(e) => { e.preventDefault(); navigateToView('checkout-login'); }} class="small">
          {t('forgotPassword.backToLogin')}
        </a>
      </p>
    </form>
  );
}
